import { Button, Modal, Input, Textarea, ActionIcon } from "rizzui";
import { useCallback, useState, useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import {
  useForm,
  Controller,
  SubmitHandler,
  useFieldArray,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Portal } from "@headlessui/react";
import EnquiryEntriesReportRepository from "@/utilities/repositories/EnquiryEntriesReport";
import {
  EnquiryReportCreateDTO,
  EnquiryReportDTO,
} from "@/utilities/types/EnquiryReport";
import toast from "react-hot-toast";
import { PiPlus, PiTrash } from "react-icons/pi";

// Validation schema for the form
const CreateReportFormSchema = z.object({
  emails: z
    .array(z.string().email("Invalid email format"))
    .optional()
    .default([]),
  phones: z
    .array(
      z
        .string()
        .min(1, "Phone number is required")
        .regex(/^[+]?[1-9][\d]{0,15}$/, "Invalid phone number format")
    )
    .optional()
    .default([]),
  alias: z.array(z.string().min(1, "Alias is required")).optional().default([]),
  description: z
    .string()
    .min(1, "Description is required")
    .max(500, "Description must be less than 500 characters"),
});

type CreateReportFormData = z.infer<typeof CreateReportFormSchema>;

type CreateReportFormProps = {
  title: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editData?: EnquiryReportDTO | null;
};

const CreateReportForm = ({
  open,
  onClose,
  onSuccess,
  editData,
  title,
}: CreateReportFormProps) => {
  const isEditMode = !!editData;
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateReportFormData>({
    resolver: zodResolver(CreateReportFormSchema),
    defaultValues: {
      emails:
        editData?.emails && editData.emails.length > 0 ? editData.emails : [],
      phones:
        editData?.phones && editData.phones.length > 0 ? editData.phones : [],
      alias: editData?.alias && editData.alias.length > 0 ? editData.alias : [],
      description: editData?.description || "",
    },
  });

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control,
    name: "emails",
  });

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({
    control,
    name: "phones",
  });

  const {
    fields: aliasFields,
    append: appendAlias,
    remove: removeAlias,
  } = useFieldArray({
    control,
    name: "alias",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when editData changes
  useEffect(() => {
    if (editData) {
      reset({
        emails:
          editData.emails && editData.emails.length > 0 ? editData.emails : [],
        phones:
          editData.phones && editData.phones.length > 0 ? editData.phones : [],
        alias:
          editData.alias && editData.alias.length > 0 ? editData.alias : [],
        description: editData.description || "",
      });
    } else {
      reset({
        emails: [],
        phones: [],
        alias: [],
        description: "",
      });
    }
  }, [editData, reset]);

  const handleClose = useCallback(() => {
    reset({
      emails: [],
      phones: [],
      alias: [],
      description: "",
    });
    onClose();
  }, [reset, onClose]);

  const onSubmit: SubmitHandler<CreateReportFormData> = useCallback(
    async (data) => {
      setIsSubmitting(true);
      try {
        // Filter out empty strings from arrays
        const cleanedData: EnquiryReportCreateDTO = {
          emails: (data.emails || []).filter((email) => email.trim() !== ""),
          phones: (data.phones || []).filter((phone) => phone.trim() !== ""),
          alias: (data.alias || []).filter((alias) => alias.trim() !== ""),
          description: data.description.trim(),
        };

        if (isEditMode && editData) {
          await EnquiryEntriesReportRepository.updateReport(
            editData.id,
            cleanedData
          );
          toast.success("Report updated successfully");
        } else {
          await EnquiryEntriesReportRepository.createReport(cleanedData);
          toast.success("Report created successfully");
        }

        handleClose();
        onSuccess?.();
      } catch (error) {
        console.error(
          `Error ${isEditMode ? "updating" : "creating"} report:`,
          error
        );
        toast.error(
          `Failed to ${isEditMode ? "update" : "create"} report. Please try again.`
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [handleClose, onSuccess, isEditMode, editData]
  );

  return (
    <Portal>
      <Modal isOpen={open} onClose={handleClose} size="lg">
        <div className="m-auto px-7 pt-6 pb-8">
          <div className="mb-7 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <ActionIcon
              size="sm"
              variant="text"
              onClick={handleClose}
              className="p-0 text-gray-500 hover:!text-gray-900"
            >
              <IoMdClose size={20} />
            </ActionIcon>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Email Addresses
              </label>
              <div className="space-y-2">
                {emailFields.length > 0 ? (
                  emailFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Controller
                        name={`emails.${index}`}
                        control={control}
                        render={({ field: inputField }) => (
                          <Input
                            {...inputField}
                            type="email"
                            placeholder="Enter email address"
                            error={errors.emails?.[index]?.message}
                            className="flex-1"
                          />
                        )}
                      />
                      <ActionIcon
                        size="sm"
                        variant="outline"
                        onClick={() => removeEmail(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <PiTrash className="h-4 w-4" />
                      </ActionIcon>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">
                    No email addresses added
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => appendEmail("")}
                  className="w-full"
                >
                  <PiPlus className="h-4 w-4 mr-1" />
                  Add Email
                </Button>
              </div>
              {errors.emails?.root && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.emails.root.message}
                </p>
              )}
            </div>

            {/* Phone Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Phone Numbers
              </label>
              <div className="space-y-2">
                {phoneFields.length > 0 ? (
                  phoneFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Controller
                        name={`phones.${index}`}
                        control={control}
                        render={({ field: inputField }) => (
                          <Input
                            {...inputField}
                            type="tel"
                            placeholder="Enter phone number"
                            error={errors.phones?.[index]?.message}
                            className="flex-1"
                          />
                        )}
                      />
                      <ActionIcon
                        size="sm"
                        variant="outline"
                        onClick={() => removePhone(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <PiTrash className="h-4 w-4" />
                      </ActionIcon>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">
                    No phone numbers added
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => appendPhone("")}
                  className="w-full"
                >
                  <PiPlus className="h-4 w-4 mr-1" />
                  Add Phone
                </Button>
              </div>
              {errors.phones?.root && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.phones.root.message}
                </p>
              )}
            </div>

            {/* Alias Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Aliases
              </label>
              <div className="space-y-2">
                {aliasFields.length > 0 ? (
                  aliasFields.map((field, index) => (
                    <div key={field.id} className="flex gap-2">
                      <Controller
                        name={`alias.${index}`}
                        control={control}
                        render={({ field: inputField }) => (
                          <Input
                            {...inputField}
                            placeholder="Enter alias"
                            error={errors.alias?.[index]?.message}
                            className="flex-1"
                          />
                        )}
                      />
                      <ActionIcon
                        size="sm"
                        variant="outline"
                        onClick={() => removeAlias(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <PiTrash className="h-4 w-4" />
                      </ActionIcon>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm">No aliases added</p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => appendAlias("")}
                  className="w-full"
                >
                  <PiPlus className="h-4 w-4 mr-1" />
                  Add Alias
                </Button>
              </div>
              {errors.alias?.root && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.alias.root.message}
                </p>
              )}
            </div>

            {/* Description Field */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Description *
              </label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    placeholder="Enter report description"
                    rows={4}
                    error={errors.description?.message}
                  />
                )}
              />
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
              >
                {isEditMode ? "Update Report" : "Create Report"}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </Portal>
  );
};

export default CreateReportForm;
